import { useState, useEffect } from 'react';
import { Crop } from 'react-image-crop';
import { ModalProps } from '../../../../../../../components/modal/modal';
import { validate } from '../../../../../../../helpers/validate';
import { useLang } from '../../../../../../../hooks/use-lang';
import { useImageStore } from '../../../../../../../store/use-image-store';
import { useQrTemplatesStore } from '../../../../../../../store/use-qr-templates-store';

export const useQrEditor = (props: ModalProps) => {
  const [uploadImage, setUploadImage] = useState<File>();
  const { t } = useLang();

  const {
    postTemplate,
    putTemplate,
    isLoading: isLoadingTemplate,
    fetchTemplates,
    deleteTemplate,
    handleUpdateTemplate,
    handleResetTemplate,
    template,
  } = useQrTemplatesStore();

  const { postImage, isLoading: isLoadingImage } = useImageStore();

  useEffect(() => {
    fetchTemplates({ is_by_company: true });

    return () => handleResetTemplate();
  }, [fetchTemplates, handleResetTemplate]);

  const handleSave = async () => {
    const isCreate = !template?.id;
    const isValid = validate({
      name: { rule: () => !!template?.name },
      img: { rule: () => (isCreate ? !!uploadImage : !!template?.image) },
    });

    if (isValid) {
      try {
        let image = template?.image;
        if (uploadImage) {
          const formData = new FormData();
          formData.append('image', uploadImage);
          const resp = await postImage(formData);
          image = resp?.normal;
        }

        if (isCreate) {
          await postTemplate({ ...template, image });
        } else {
          await putTemplate({ ...template, image });
        }
        props.onClose();
      } catch (e) {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    if (!uploadImage) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      handleUpdateTemplate({ image: reader.result as string });
    };
    reader.readAsDataURL(uploadImage);

    return () => reader.abort();
  }, [handleUpdateTemplate, uploadImage]);

  const handleUpdateCrop = (c: Crop) =>
    handleUpdateTemplate({
      ...template,
      crop: c,
    });

  const handleDeleteTemplate = () => {
    if (template?.id) {
      deleteTemplate(template?.id);
      handleResetTemplate();
    }
  };

  return {
    handleSave,
    isLoadingTemplate,
    template,
    isLoadingImage,
    t,
    handleUpdateCrop,
    handleDeleteTemplate,
    setUploadImage,
    uploadImage,
  };
};
